import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { Observable, forkJoin } from 'rxjs';

import { Role } from '../../core/auth/models/role';
import { ContextService } from '../../core/context.service';
import { LanguageService } from '../../core/i18n/language.service';
import { NumberInput } from '../../shared/components/forms/number-input';
import { Select } from '../../shared/components/forms/select';
import { TextInput } from '../../shared/components/forms/text-input';
import { Toggle } from '../../shared/components/forms/toggle';
import { Checkbox } from '../../shared/components/forms/checkbox';
import { SegmentedControl, Segment } from '../../shared/components/forms/segmented-control';
import { ToastService } from '../../shared/services/toast-service';
import { AlertsNotification, TenantSetting } from './models/tenant-setting.model';
import { TenantSettingsService } from './services/tenant-settings.service';
import { SettingRow } from './setting-row.component';
import { SettingSection } from './setting-section.component';
import { LOCALE_OPTIONS, TIMEZONE_OPTIONS, localeLabel } from './settings-options';
import { Button } from '../../shared/components/button/button';

type SectionId = 'alerts' | 'retention' | 'weather' | 'userDefaults';

/** Flat editing model the API settings are mapped into. The composite
 *  alerts.notification setting is split into one field per control. */
interface SettingsForm {
  offlineMin: number | null;
  batteryPct: number | null;
  alertsEnabled: boolean;
  chEmail: boolean;
  chWebhook: boolean;
  recipients: string;
  webhookUrl: string;
  logsDays: number | null;
  snapshotsDays: number | null;
  weatherLocation: string;
  weatherUnits: string;
  locale: string;
  timezone: string;
}

const KEY = {
  offlineMin: 'device.offlineThresholdMinutes',
  batteryPct: 'device.batteryLowThresholdPct',
  alerts: 'alerts.notification',
  logsDays: 'retention.deviceLogsDays',
  snapshotsDays: 'retention.snapshotsDays',
  weatherLocation: 'weather.location',
  weatherUnits: 'weather.units',
  locale: 'locale',
  timezone: 'timezone',
} as const;

const SECTIONS: SectionId[] = ['alerts', 'retention', 'weather', 'userDefaults'];

const SECTION_KEYS: Record<SectionId, string[]> = {
  alerts: [KEY.alerts],
  retention: [KEY.logsDays, KEY.snapshotsDays],
  weather: [KEY.weatherLocation, KEY.weatherUnits],
  userDefaults: [KEY.locale, KEY.timezone],
};

const KEY_FIELDS: Record<string, (keyof SettingsForm)[]> = {
  [KEY.offlineMin]: ['offlineMin'],
  [KEY.batteryPct]: ['batteryPct'],
  [KEY.alerts]: ['alertsEnabled', 'chEmail', 'chWebhook', 'recipients', 'webhookUrl'],
  [KEY.logsDays]: ['logsDays'],
  [KEY.snapshotsDays]: ['snapshotsDays'],
  [KEY.weatherLocation]: ['weatherLocation'],
  [KEY.weatherUnits]: ['weatherUnits'],
  [KEY.locale]: ['locale'],
  [KEY.timezone]: ['timezone'],
};

/** How far below the scroll container's top a section counts as "current". */
const SPY_OFFSET = 140;

/**
 * Tenant settings page (design: "Tenant Settings.dc.html"). One card per
 * settings category with per-section save/discard, per-row reset-to-default,
 * a scroll-spied sub navigation, and a read-only mode for roles below
 * tenant-admin. Saving a value back to the platform default removes the
 * override (DELETE) instead of storing an identical copy (PUT), so later
 * default changes keep flowing through.
 */
@Component({
  selector: 'app-tenant-settings',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Button,
    FormsModule,
    TranslocoDirective,
    NumberInput,
    TextInput,
    Select,
    Toggle,
    Checkbox,
    SegmentedControl,
    SettingRow,
    SettingSection,
  ],
  templateUrl: './tenant-settings.component.html',
})
export class TenantSettingsComponent implements OnInit, OnDestroy {
  private readonly service = inject(TenantSettingsService);
  private readonly context = inject(ContextService);
  private readonly toast = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly route = inject(ActivatedRoute);

  protected readonly sections = SECTIONS;
  protected readonly localeOptions = LOCALE_OPTIONS;
  protected readonly timezoneOptions = TIMEZONE_OPTIONS;
  protected readonly unitOptions = [
    { value: 'metric', labelKey: 'tenantSettings.weather.units.metric' },
    { value: 'imperial', labelKey: 'tenantSettings.weather.units.imperial' },
  ] as const;

  /** Units options as segmented-control segments; re-translates on switch. */
  protected readonly unitSegments = computed<Segment[]>(() => {
    this.languageService.current();
    return this.unitOptions.map((o) => ({
      value: o.value,
      label: this.transloco.translate(o.labelKey),
    }));
  });

  protected readonly loading = signal(true);
  protected readonly loadFailed = signal(false);
  /** Working copy bound to the controls. */
  protected readonly form = signal<SettingsForm | null>(null);
  /** Last state confirmed by the server; dirty = form vs savedForm. */
  private readonly savedForm = signal<SettingsForm | null>(null);
  /** Platform defaults, for the captions and the reset links. */
  private readonly defaults = signal<SettingsForm | null>(null);
  private readonly settings = signal<TenantSetting[]>([]);
  protected readonly savingSection = signal<SectionId | null>(null);
  protected readonly savedNotes = signal<Partial<Record<SectionId, true>>>({});
  protected readonly activeSection = signal<SectionId>('alerts');

  private readonly roles = toSignal(this.context.roles$, { initialValue: [] as string[] });
  private readonly organization = toSignal(this.context.organization$, { initialValue: null });

  protected readonly readOnly = computed(() => {
    const roles = this.roles();
    return !roles.includes(Role.TENANT_ADMIN) && !roles.includes(Role.SUPER_ADMIN);
  });
  protected readonly tenantName = computed(() => this.organization()?.name ?? '');
  protected readonly roleLabelKey = computed(() =>
    this.roles().includes(Role.OPERATOR)
      ? 'tenantSettings.roleOperator'
      : 'tenantSettings.roleViewer',
  );

  protected readonly fieldInvalid = computed(() => {
    const f = this.form();
    return {
      offlineMin: f !== null && !isIntBetween(f.offlineMin, 1),
      batteryPct: f !== null && !isIntBetween(f.batteryPct, 0, 100),
      logsDays: f !== null && !isIntBetween(f.logsDays, 1),
      snapshotsDays: f !== null && !isIntBetween(f.snapshotsDays, 1),
    };
  });

  protected readonly invalid = computed<Record<SectionId, boolean>>(() => {
    const fi = this.fieldInvalid();
    return {
      alerts: false,
      retention: fi.logsDays || fi.snapshotsDays,
      weather: false,
      userDefaults: false,
    };
  });

  protected readonly dirty = computed<Record<SectionId, boolean>>(() => {
    const form = this.form();
    const saved = this.savedForm();
    const result: Record<SectionId, boolean> = {
      alerts: false,
      retention: false,
      weather: false,
      userDefaults: false,
    };
    if (!form || !saved || this.readOnly()) return result;
    for (const id of SECTIONS) {
      result[id] = sectionFields(id).some((f) => form[f] !== saved[f]);
    }
    return result;
  });

  /** "Platform default: …" captions, composed per row from the registry defaults. */
  protected readonly captions = computed(() => {
    this.languageService.current(); // re-evaluate on language switch
    const d = this.defaults();
    if (!d) return null;
    const t = (key: string) => this.transloco.translate(key);
    const def = (value: string) =>
      this.transloco.translate('tenantSettings.platformDefault', { value });
    return {
      offlineMin: def(`${d.offlineMin} ${t('tenantSettings.units.minutes')}`),
      batteryPct: def(`${d.batteryPct}${t('tenantSettings.units.percent')}`),
      alerts: def(t(d.alertsEnabled ? 'tenantSettings.alerts.enabled.on' : 'tenantSettings.alerts.enabled.off')),
      logsDays: def(`${d.logsDays} ${t('tenantSettings.units.days')}`),
      snapshotsDays: def(`${d.snapshotsDays} ${t('tenantSettings.units.days')}`),
      weatherLocation: def(d.weatherLocation || t('tenantSettings.weather.location.ownSite')),
      weatherUnits: def(
        t(d.weatherUnits === 'imperial' ? 'tenantSettings.weather.units.imperial' : 'tenantSettings.weather.units.metric'),
      ),
      locale: def(localeLabel(d.locale)),
      timezone: def(d.timezone),
    };
  });

  // Channels only make sense while notifications are on; each address field
  // additionally needs its channel ticked (mirrors the design's disable chain).
  protected readonly channelsDisabled = computed(() => this.readOnly() || !this.form()?.alertsEnabled);
  protected readonly recipientsDisabled = computed(() => this.channelsDisabled() || !this.form()?.chEmail);
  protected readonly webhookDisabled = computed(() => this.channelsDisabled() || !this.form()?.chWebhook);

  /** Suppresses the scroll spy while a nav click is smooth-scrolling. */
  private spyLocked = false;
  private spyUnlockTimer?: ReturnType<typeof setTimeout>;
  private readonly savedTimers = new Map<SectionId, ReturnType<typeof setTimeout>>();

  ngOnInit(): void {
    this.fetch();
    // Scroll events don't bubble, so listen on the capture phase to catch the
    // app shell's <main> scrolling without coupling to it directly.
    window.addEventListener('scroll', this.onAnyScroll, { capture: true, passive: true });
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.onAnyScroll, { capture: true });
    clearTimeout(this.spyUnlockTimer);
    this.savedTimers.forEach((timer) => clearTimeout(timer));
  }

  protected retry(): void {
    this.fetch();
  }

  protected patch<K extends keyof SettingsForm>(key: K, value: SettingsForm[K]): void {
    this.form.update((f) => (f ? { ...f, [key]: value } : f));
  }

  /** True when any of the fields differs from the platform default (drives the reset link). */
  protected differs(...fields: (keyof SettingsForm)[]): boolean {
    const form = this.form();
    const defaults = this.defaults();
    if (!form || !defaults || this.readOnly()) return false;
    return fields.some((f) => form[f] !== defaults[f]);
  }

  /** Restores the given fields to the platform default in the working copy (still needs Save). */
  protected resetToDefault(...fields: (keyof SettingsForm)[]): void {
    const defaults = this.defaults();
    if (!defaults) return;
    this.form.update((f) => {
      if (!f) return f;
      const next = { ...f };
      for (const field of fields) {
        (next as Record<string, unknown>)[field] = defaults[field];
      }
      return next;
    });
  }

  protected discardSection(id: SectionId): void {
    const saved = this.savedForm();
    if (!saved) return;
    this.form.update((f) => {
      if (!f) return f;
      const next = { ...f };
      for (const field of sectionFields(id)) {
        (next as Record<string, unknown>)[field] = saved[field];
      }
      return next;
    });
  }

  protected saveSection(id: SectionId): void {
    const form = this.form();
    const saved = this.savedForm();
    if (!form || !saved || this.invalid()[id] || this.savingSection() !== null) return;

    const ops: Observable<unknown>[] = [];
    const pendingPatch: Record<string, unknown> = {};
    for (const key of SECTION_KEYS[id]) {
      if (!KEY_FIELDS[key].some((f) => form[f] !== saved[f])) continue;
      const value = payloadFor(key, form);
      const setting = this.settings().find((s) => s.key === key);
      // Back at the default? Remove the override instead of storing a copy.
      ops.push(
        setting && deepEqual(value, setting.defaultValue)
          ? this.service.reset(key)
          : this.service.update(key, value),
      );
      pendingPatch[key] = value;
    }
    if (ops.length === 0) return;

    this.savingSection.set(id);
    forkJoin(ops)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.savingSection.set(null);
          this.context.patchSettings(pendingPatch);
          this.savedForm.update((sv) => {
            if (!sv) return sv;
            const next = { ...sv };
            for (const field of sectionFields(id)) {
              (next as Record<string, unknown>)[field] = form[field];
            }
            return next;
          });
          this.flashSaved(id);
        },
        error: (err: unknown) => {
          this.savingSection.set(null);
          // 400 carries the backend's validation reason; surface it verbatim.
          const apiMessage = (err as { error?: { message?: string } })?.error?.message;
          this.toast.show(
            apiMessage || this.transloco.translate<string>('tenantSettings.toast.saveFailed'),
            'error',
          );
          // A multi-key batch can partially succeed; re-sync the persisted
          // baseline while keeping the user's edits in the working copy.
          this.fetch(true);
        },
      });
  }

  protected goTo(id: SectionId): void {
    const el = document.getElementById('sec-' + id);
    if (!el) return;
    this.activeSection.set(id);
    this.spyLocked = true;
    clearTimeout(this.spyUnlockTimer);
    this.spyUnlockTimer = setTimeout(() => (this.spyLocked = false), 700);
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private readonly onAnyScroll = (event: Event): void => {
    if (this.spyLocked) return;
    const container = event.target;
    if (!(container instanceof HTMLElement) || !container.contains(this.host.nativeElement)) {
      return;
    }
    const containerTop = container.getBoundingClientRect().top;
    let active: SectionId = SECTIONS[0];
    for (const id of SECTIONS) {
      const el = document.getElementById('sec-' + id);
      if (el && el.getBoundingClientRect().top - containerTop <= SPY_OFFSET) active = id;
    }
    // The last section may never reach the spy band; at the bottom it wins.
    if (container.scrollTop + container.clientHeight >= container.scrollHeight - 8) {
      active = SECTIONS[SECTIONS.length - 1];
    }
    if (active !== this.activeSection()) this.activeSection.set(active);
  };

  private fetch(preserveForm = false): void {
    if (!preserveForm) {
      this.loading.set(true);
      this.loadFailed.set(false);
    }
    this.service
      .getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const byKey = new Map(res.data.map((s) => [s.key, s]));
          this.settings.set(res.data);
          const saved = buildForm((key) => byKey.get(key)?.value);
          this.savedForm.set(saved);
          this.defaults.set(buildForm((key) => byKey.get(key)?.defaultValue));
          const firstLoad = !preserveForm || !this.form();
          if (firstLoad) this.form.set({ ...saved });
          this.loading.set(false);
          // Arriving from the preferences page with a #fragment: scroll to
          // that section once the cards have rendered.
          const fragment = this.route.snapshot.fragment as SectionId | null;
          if (firstLoad && fragment && SECTIONS.includes(fragment)) {
            setTimeout(() => this.goTo(fragment));
          }
        },
        error: () => {
          this.loading.set(false);
          if (!preserveForm) this.loadFailed.set(true);
          this.toast.show(this.transloco.translate('tenantSettings.toast.loadFailed'), 'error');
        },
      });
  }

  private flashSaved(id: SectionId): void {
    this.savedNotes.update((n) => ({ ...n, [id]: true as const }));
    clearTimeout(this.savedTimers.get(id));
    this.savedTimers.set(
      id,
      setTimeout(() => {
        this.savedNotes.update((n) => {
          const next = { ...n };
          delete next[id];
          return next;
        });
      }, 2400),
    );
  }
}

function sectionFields(id: SectionId): (keyof SettingsForm)[] {
  return SECTION_KEYS[id].flatMap((key) => KEY_FIELDS[key]);
}

/** Maps the raw settings (by value or defaultValue) into the flat form model. */
function buildForm(get: (key: string) => unknown): SettingsForm {
  const alerts = (get(KEY.alerts) ?? {}) as Partial<AlertsNotification>;
  const channels = alerts.channels ?? [];
  return {
    offlineMin: toNumber(get(KEY.offlineMin)),
    batteryPct: toNumber(get(KEY.batteryPct)),
    alertsEnabled: alerts.enabled === true,
    chEmail: channels.includes('email'),
    chWebhook: channels.includes('webhook'),
    recipients: (alerts.recipients ?? []).join(', '),
    webhookUrl: alerts.webhookUrl ?? '',
    logsDays: toNumber(get(KEY.logsDays)),
    snapshotsDays: toNumber(get(KEY.snapshotsDays)),
    weatherLocation: toString(get(KEY.weatherLocation)),
    weatherUnits: toString(get(KEY.weatherUnits)) || 'metric',
    locale: toString(get(KEY.locale)) || 'en-GB',
    timezone: toString(get(KEY.timezone)) || 'UTC',
  };
}

/** Builds the raw JSON value PUT to the API for one setting key. */
function payloadFor(key: string, f: SettingsForm): unknown {
  switch (key) {
    case KEY.offlineMin:
      return f.offlineMin;
    case KEY.batteryPct:
      return f.batteryPct;
    case KEY.alerts: {
      const channels: string[] = [];
      if (f.chEmail) channels.push('email');
      if (f.chWebhook) channels.push('webhook');
      const value: AlertsNotification = {
        enabled: f.alertsEnabled,
        channels,
        recipients: f.recipients
          .split(',')
          .map((r) => r.trim())
          .filter(Boolean),
        webhookUrl: f.webhookUrl.trim() || null,
      };
      return value;
    }
    case KEY.logsDays:
      return f.logsDays;
    case KEY.snapshotsDays:
      return f.snapshotsDays;
    case KEY.weatherLocation:
      return f.weatherLocation.trim();
    case KEY.weatherUnits:
      return f.weatherUnits;
    case KEY.locale:
      return f.locale;
    case KEY.timezone:
      return f.timezone;
    default:
      return null;
  }
}

function toNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function toString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function isIntBetween(value: number | null, min: number, max = Number.POSITIVE_INFINITY): boolean {
  return value !== null && Number.isInteger(value) && value >= min && value <= max;
}

/** Structural equality for raw JSON setting values (array order matters). */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== typeof b) return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((v, i) => deepEqual(v, b[i]))
    );
  }
  if (typeof a === 'object') {
    const aKeys = Object.keys(a as object);
    const bKeys = Object.keys(b as object);
    return (
      aKeys.length === bKeys.length &&
      aKeys.every((k) =>
        deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]),
      )
    );
  }
  return false;
}
